/**
 * Public Package Page — read-only view of a saved BeeWeddy package.
 *
 * Anyone with the link can see this (no auth required). The matching
 * backend endpoint `GET /api/p/{pkg_id}/html` produces the same content
 * wrapped in OG / Twitter Card meta tags so WhatsApp / Facebook / X /
 * LinkedIn show a rich preview when the link is shared.
 *
 * Owner CTA: if the currently-logged-in user is the owner of the package
 * (matched by phone), an "Edit / Modify" button takes them to the
 * Instant Pricing page in edit-mode.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Cake, MapPin, CalendarBlank, Users, Tag, WhatsappLogo, LinkSimple, PencilSimple,
  Spinner, ArrowRight, Hourglass,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import BrandLogo from '../components/BrandLogo';
import Footer from '../components/Footer';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PublicPackagePage = () => {
  const { pkgId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let alive = true;
    axios.get(`${BACKEND_URL}/api/p/${pkgId}`)
      .then(({ data }) => { if (alive) setPkg(data); })
      .catch((err) => {
        if (alive) {
          if (err?.response?.status === 404) setNotFound(true);
          else toast.error('Could not load package');
        }
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [pkgId]);

  // Is the current logged-in user the owner of this package? We can't expose
  // the owner phone in the public payload (PII) — instead we ask the
  // authenticated endpoint and match by id.
  const [isOwner, setIsOwner] = useState(false);
  useEffect(() => {
    if (!user) return;
    axios.get(`${BACKEND_URL}/api/packages`, { withCredentials: true })
      .then(({ data }) => {
        const mine = (data || []).some((p) => p.id === pkgId);
        setIsOwner(mine);
      })
      .catch(() => setIsOwner(false));
  }, [user, pkgId]);

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-sand-50">
        <Spinner size={28} className="animate-spin text-brand-primary" />
      </div>
    );
  }
  if (notFound) {
    return (
      <div className="min-h-screen grid place-items-center bg-sand-50 p-6 text-center"
        data-testid="public-pkg-not-found">
        <div>
          <Hourglass size={42} className="mx-auto text-stone-400 mb-4" />
          <h1 className="font-heading text-3xl mb-2">Package not found</h1>
          <p className="text-text-secondary mb-6">
            This link is invalid or the package was deleted.
          </p>
          <Button onClick={() => navigate('/')}
            className="rounded-none bg-brand-primary hover:bg-[#A84A38] text-sand-50">
            Go to BeeWeddy <ArrowRight size={14} className="ml-1" />
          </Button>
        </div>
      </div>
    );
  }

  const title = `${pkg.package_name} — BeeWeddy`;
  const description = [
    pkg.event_type ? pkg.event_type.charAt(0).toUpperCase() + pkg.event_type.slice(1) : null,
    pkg.guests ? `${pkg.guests} guests` : null,
    pkg.event_city,
    pkg.total ? `₹${Math.round(pkg.total).toLocaleString('en-IN')}` : null,
  ].filter(Boolean).join(' · ');

  const canonicalUrl = `${window.location.origin}/api/p/${pkgId}/html`;

  const handleWhatsApp = () => {
    const msg = [
      `Check out my ${pkg.event_type || 'event'} package on BeeWeddy!`,
      `${pkg.package_name} — ₹${Math.round(pkg.total || 0).toLocaleString('en-IN')}`,
      pkg.event_date ? `Event: ${pkg.event_date}` : '',
      pkg.event_city ? `Location: ${pkg.event_city}` : '',
      canonicalUrl,
    ].filter(Boolean).join('\n');
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank', 'noopener');
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(canonicalUrl);
      toast.success('Link copied');
    } catch {
      toast.error('Could not copy link');
    }
  };

  const handleEdit = () => {
    // Pre-load Instant Pricing in edit-mode for this package id
    try {
      localStorage.setItem('eventbook_edit_package_id', pkgId);
    } catch { /* noop */ }
    navigate('/instant-pricing');
  };

  // Group items by category for clean rendering
  const byCategory = {};
  (pkg.items || []).forEach((it) => {
    const cat = it.category || 'misc';
    if (!byCategory[cat]) byCategory[cat] = [];
    byCategory[cat].push(it);
  });

  return (
    <div className="min-h-screen bg-sand-50">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={`${window.location.origin}/og-default.png`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
      </Helmet>

      <header className="border-b border-[#E8E1D5] bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" data-testid="public-pkg-home-link">
            <BrandLogo />
          </Link>
          {!user && (
            <Button variant="outline" onClick={() => navigate('/login')}
              className="rounded-none border-[#E8E1D5]"
              data-testid="public-pkg-login-cta">
              Log in
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8" data-testid="public-pkg-main">
        <Card className="bg-white border-[#E8E1D5]">
          <CardContent className="p-6 space-y-6">
            <div>
              <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-brand-primary">
                Event package
              </p>
              <h1 className="font-heading text-3xl sm:text-4xl mt-1" data-testid="public-pkg-title">
                {pkg.package_name}
              </h1>
              <div className="flex flex-wrap gap-3 mt-3 text-sm text-text-secondary">
                {pkg.event_type && (
                  <span className="inline-flex items-center gap-1.5">
                    <Tag size={14} /> {pkg.event_type.charAt(0).toUpperCase() + pkg.event_type.slice(1)}
                    {pkg.marriage_type ? ` · ${pkg.marriage_type}` : ''}
                  </span>
                )}
                {pkg.event_date && (
                  <span className="inline-flex items-center gap-1.5">
                    <CalendarBlank size={14} /> {pkg.event_date}
                  </span>
                )}
                {pkg.event_city && (
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin size={14} /> {pkg.event_city}
                  </span>
                )}
                {pkg.guests > 0 && (
                  <span className="inline-flex items-center gap-1.5">
                    <Users size={14} /> {pkg.guests} guests
                  </span>
                )}
              </div>
            </div>

            {/* Item list, grouped by category */}
            <div className="space-y-4">
              {Object.entries(byCategory).map(([cat, items]) => (
                <div key={cat}>
                  <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-text-secondary mb-2">
                    {cat.replace(/_/g, ' ')}
                  </div>
                  <ul className="space-y-1.5">
                    {items.map((it, idx) => (
                      <li key={`${cat}-${idx}`}
                        className="flex justify-between gap-3 py-1.5 border-b border-[#F5EFE5] text-sm"
                        data-testid={`public-pkg-item-${cat}-${idx}`}>
                        <div className="min-w-0">
                          <div className="truncate">{it.name}</div>
                          {it.vendor_name && (
                            <div className="text-[11px] text-text-secondary truncate">by {it.vendor_name}</div>
                          )}
                        </div>
                        <div className="shrink-0 font-semibold">
                          ₹{Math.round(it.price || 0).toLocaleString('en-IN')}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* Total */}
            <div className="flex items-end justify-between pt-4 border-t-2 border-[#E8E1D5]">
              <span className="text-sm text-text-secondary">Estimated total</span>
              <span className="font-heading text-3xl text-brand-primary" data-testid="public-pkg-total">
                ₹{Math.round(pkg.total || 0).toLocaleString('en-IN')}
              </span>
            </div>

            {/* Share + owner CTAs */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2">
              <Button onClick={handleWhatsApp}
                className="rounded-none bg-green-600 hover:bg-green-700 text-white"
                data-testid="public-pkg-share-whatsapp">
                <WhatsappLogo size={16} className="mr-2" weight="fill" /> Share to WhatsApp
              </Button>
              <Button onClick={handleCopy}
                variant="outline"
                className="rounded-none border-[#E8E1D5]"
                data-testid="public-pkg-copy-link">
                <LinkSimple size={16} className="mr-2" /> Copy link
              </Button>
            </div>

            {isOwner && (
              <Button onClick={handleEdit}
                className="w-full rounded-none bg-brand-primary hover:bg-[#A84A38] text-sand-50"
                data-testid="public-pkg-edit-cta">
                <PencilSimple size={16} className="mr-2" /> Edit / Modify this package
              </Button>
            )}

            {!user && (
              <p className="text-xs text-text-secondary text-center pt-2">
                Estimates are indicative. Want one for your event?{' '}
                <Link to="/instant-pricing" className="underline text-brand-primary font-medium"
                  data-testid="public-pkg-build-own-link">
                  Build your own in 60 seconds →
                </Link>
              </p>
            )}
          </CardContent>
        </Card>
      </main>
      <Footer />
    </div>
  );
};

export default PublicPackagePage;
